import React from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarType, FieldType } from '../../../models';
import { Fragment } from '../../FlightsManager/MobileFragments/Fragment';
import { FragmentBody } from '../../FlightsManager/MobileFragments/FragmentBody';
import { NextButton } from '../../FlightsManager/MobileFragments/NextButton';
import { ConnectedFieldHeader } from '../../FlightsManager/MobileFragments/FragmentHeader/FieldHeader';
import { FieldHeaderPrisma } from '../../FlightsManager/MobileFragments/FragmentHeader/FieldHeaderPrisma';
import { DatePickerVertical } from '../../../components/DatePicker/DatePickerVertical';
import { ABUtils, Config, DateUtils } from '../../../utils';
import Field from '../../FlightsManager/MobileFields/Field';
import { getFormattedDate } from '../../FlightsManager/MobileFields/DateFieldRoundTrip';
import { ConnectedSeparateStayDateRangeMobile } from './SeparateStayDateRangeMobile';

const SeparateStayDateFragmentHeader = () => {
    const { t } = useTranslation();
    const { isCssVariationMobile } = ABUtils.getABs();
    const headerText = `${t('flightsManager.separateStay.checkIn')} / ${t('flightsManager.separateStay.checkOut')}`;
    if (isCssVariationMobile) {
        return <FieldHeaderPrisma text={headerText} />;
    }
    return <ConnectedFieldHeader />;
};

const getPickerMaxDate = ({ isReturn, isDone, maxDate }) =>
    isReturn || isDone ? maxDate : DateUtils.add(maxDate, -1);

const SeparateStayDateFragmentInner = ({ onSelectValue, dates, minDate, maxDate }) => {
    const hasCheckIn = !!dates[0];
    const hasCheckOut = !!dates[1];
    const isDone = hasCheckIn && hasCheckOut;
    const isReturn = hasCheckIn && !hasCheckOut;
    // stop=true → stay on slide; stop=false → advance
    const stop = !hasCheckIn || isDone;
    const direction = Config.get('isRtl') ? 'rtl' : 'ltr';
    const pickerMinDate = isReturn ? DateUtils.add(dates[0], 1) : minDate;
    const pickerMaxDate = getPickerMaxDate({ isReturn, isDone, maxDate });

    const handleChange = (date) => {
        if (isDone) {
            onSelectValue(date, true);
        } else {
            onSelectValue(date, stop);
        }
    };

    return (
        <React.Fragment>
            <SeparateStayDateFragmentHeader />
            <FragmentBody padding={false}>
                <DatePickerVertical
                    dates={dates}
                    value={isReturn ? dates[0] : null}
                    minDate={pickerMinDate}
                    maxDate={pickerMaxDate}
                    onChange={handleChange}
                    isDatesRangeShown
                    type={isReturn ? CalendarType.RETURN : CalendarType.DEPARTURE}
                    direction={direction}
                />
            </FragmentBody>
            {isDone && <NextButton testId="separate-stay-date-next-btn" />}
        </React.Fragment>
    );
};

export const SeparateStayDateFieldMobile = () => {
    const { t } = useTranslation();
    const dateFormat = t('calendar.dateFormatOneLiner');
    const direction = Config.get('isRtl') ? 'rtl' : 'ltr';
    const checkInText = t('flightsManager.separateStay.checkIn');
    const checkOutText = t('flightsManager.separateStay.checkOut');
    const emptyPlaceholder = t('flightsManager.separateStay.dateOfStay');

    return (
        <ConnectedSeparateStayDateRangeMobile>
            {({ dates, minDate, maxDate, setValue, enabled }) => {
                if (!enabled) return null;

                const isDone = !!dates[0] && !!dates[1];
                const value = getFormattedDate(dates, dateFormat, direction);
                const placeholder = value
                    ? `${checkInText} / ${checkOutText}`
                    : emptyPlaceholder;

                return (
                    <>
                        <Field
                            icon="calendar"
                            id={FieldType.SEPARATE_STAY}
                            placeholder={placeholder}
                            value={value}
                        />
                        <Fragment
                            id={FieldType.SEPARATE_STAY}
                            isDone={isDone}
                            isEmpty={!isDone}
                            setValue={setValue}
                        >
                            {({ onSelectValue }) => (
                                <SeparateStayDateFragmentInner
                                    onSelectValue={onSelectValue}
                                    dates={dates}
                                    minDate={minDate}
                                    maxDate={maxDate}
                                />
                            )}
                        </Fragment>
                    </>
                );
            }}
        </ConnectedSeparateStayDateRangeMobile>
    );
};
